import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import PermissionContainer from "./permissioncontainer";
import PermissionForm from "./permissionform";
import { handleValidDate, handleValidTime } from "helpers/common_helper";

import NewDeleteModal from "Components/Common/NewDeleteModal";
import { toast } from "react-toastify";

import {
  fetchPermissionsThunk,
  addPermissionThunk,
  updatePermissionThunk,
  deletePermissionThunk,
} from "slices/RBAC/thunk";

import { Link, Navigate, useNavigate } from "react-router-dom";
import { usePermissions } from "../../../hooks/usePermissions";
import { PermissionEnum } from "../../../Components/constants/permissions";

const selectRBACState = (state: RootState) => (state as any).rbac;

const rbacSelector = createSelector(selectRBACState, (rbac) => ({
  permissions: rbac.permissions,
  loading: rbac.loading,
}));

const PermissionPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { permissions, loading } = useSelector(rbacSelector);
  const userPermissions = usePermissions();

  const permissionList = permissions || [];

  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editPermissionData, setEditPermissionData] = useState<any>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPermissionId, setSelectedPermissionId] = useState<number | null>(null);

  const loadPermissionList = useCallback(() => {
    return dispatch(fetchPermissionsThunk());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchPermissionsThunk());
  }, [dispatch]);

  const navigate = useNavigate();

  const handleDeleteClick = (id: number) => {
    setSelectedPermissionId(id);
    setDeleteModalOpen(true);
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        permissionid: isEdit
          ? Yup.number().optional()
          : Yup.number()
            .required("Permission ID is required")
            .max(9999, "Maximum 4 digits allowed")
            .test(
              "max-digits",
              "Maximum 4 digits allowed",
              function (value) {
                if (!value) return true;
                return value.toString().length <= 4;
              }
            ),
        permissionname: Yup.string()
          .trim()
          .required("Permission name is required")
          .matches(/^[a-zA-Z_/\s-]+$/, "Permission name can only contain Characters")
          .max(70, "Maximum 70 characters allowed"),
        description: Yup.string()
          .trim()
          .required("Description is required")
          .matches(/^[a-zA-Z_/\s-]+$/, "Description can only contain Characters")
          .max(100, "Maximum 100 characters allowed"),
        status: isEdit
          ? Yup.number().required("Status is required")
          : Yup.number().optional(),
      }),
    [isEdit]
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      permissionid: "",
      permissionname: "",
      description: "",
      status: 1,
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Close modal immediately
      setIsModalOpen(false);
      resetForm();
      const editData = editPermissionData;
      const isEditMode = isEdit;
      setIsEdit(false);
      setEditPermissionData(null);

      try {
        if (isEditMode && editData) {
          const res = await dispatch(
            updatePermissionThunk(editData.permissionid, {
              permissionname: values.permissionname,
              description: values.description,
              status: values.status || 1,
            })
          );
          if (!res?.success) {
            toast.error(res?.message || "Failed to update permission");
            return;
          }
          toast.success("Permission updated successfully!");
        } else {
          const res = await dispatch(
            addPermissionThunk({
              permissionid: Number(values.permissionid) || 0,
              permissionname: values.permissionname,
              description: values.description,
            })
          );
          if (!res?.success) {
            toast.error(res?.message || "Failed to add permission");
            return;
          }
          toast.success("Permission added successfully!");
        }

        await dispatch(fetchPermissionsThunk());
      } catch (err) {
        console.error("Error saving permission:", err);
        toast.error("An error occurred. Please try again.");
      }
    },
  });

  const toggleModal = () => {
    if (!isModalOpen) {
      validation.resetForm();
      setIsEdit(false);
      setEditPermissionData(null);
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleRowClick = useCallback((row: any) => {}, []);

  const handleEditClick = useCallback(
    (row: any) => {
      setIsEdit(true);
      setEditPermissionData(row);
      setIsModalOpen(true);

      validation.setValues({
        permissionid: row.permissionid || 0,
        permissionname: row.permissionname || "",
        description: row.description || "",
        status: row.status === "Active" ? 1 : 2,
      });
    },
    [validation]
  );

  const filteredPermissions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return permissionList;
    return permissionList.filter((t: any) =>
      t.permissionname?.toLowerCase().includes(q)
    );
  }, [permissionList, searchQuery]);

  const columns = useMemo(
    () => [
      {
        header: "Permission ID",
        accessorKey: "permissionid",
        cell: ({ row }: any) => {
          const id = row.original.permissionid;
          return `#ID${String(id).padStart(3, "0")}`;
        },
      },
      { header: "Permission Name", accessorKey: "permissionname" },
      { header: "Description", accessorKey: "description" },
      {
        header: "Created On",
        accessorKey: "createdon",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? (
            <>
              {handleValidDate(value)},{" "}
              <small className="text-muted">{handleValidTime(value)}</small>
            </>
          ) : (
            <>N/A</>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (cell: any) => {
          const value = cell.getValue();
          const lowerVal = value?.toString().toLowerCase();
          const statusColor =
            lowerVal === "active" ? "#159E3A" : "#A65F00";

          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                color: statusColor,
                border: `1px solid ${statusColor}`,
                textTransform: "capitalize",
              }}
            >
              {value}
            </span>
          );
        },
      },
      { header: "Created By", accessorKey: "createdby" },
      ...(userPermissions.includes(PermissionEnum.UPDATE_PERMISSION) || userPermissions.includes(PermissionEnum.DELETE_PERMISSION) ? [{
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const row = cellProps.row.original;

          return (
            <ul className="list-inline d-flex justify-content-center align-items-center mb-0">
              {/* <li className="list-inline-item" title="Edit">
                <Button
                  color=""
                  className="btn btn-icon btn-soft-primary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(row);
                  }}
                >
                  <i className="ri-pencil-fill"></i>
                </Button>
              </li> */}

              {/* Delete */}
              {userPermissions.includes(PermissionEnum.DELETE_PERMISSION) && (
                <li className="list-inline-item" title="Delete">
                  <Button
                    color=""
                    className="btn btn-icon btn-soft-danger btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(row.permissionid);  
                    }}
                  >
                    <i className="ri-delete-bin-line"></i>
                  </Button>
                </li>
              )}
            </ul>
          );
        },
      }] : []),
    ],
    [handleDeleteClick, userPermissions]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="Permission" pageTitle={["RBAC", "Permissions"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={3}>
            {/* <div className="position-relative">
              <Input
                type="text"
                className="form-control border ps-5 ms-2 rounded-3"
                value={searchQuery}
                placeholder="Search Permission..."
                onChange={(e) => setSearchQuery(e.target.value)}
              /> */}
              {/* <i
                className="ri-search-line position-absolute top-50 start-0 translate-middle-y text-muted ms-3"
                style={{ fontSize: "1.1rem" }}
              />
            </div> */}
          </Col>
          <Col md={9} className="text-end">
          {userPermissions.includes(PermissionEnum.ADD_PERMISSION) && (
            <Button color="primary" onClick={toggleModal}>
              + Add New
            </Button>
          )}
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  filteredPermissions.length > 0 ? (
                    <PermissionContainer
                      columns={columns}
                      data={filteredPermissions}
                      customPageSize={10000}
                      limit={10000}
                      setPage={() => {}}
                      onRowClick={handleRowClick}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white"
                      divClass="table-responsive table-card"
                    />
                  ) : (
                    <div className="text-center p-4">
                      No records found.
                    </div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal}>
          {isEdit ? "Edit Permission" : "Add New Permission"}
        </ModalHeader>
        <form onSubmit={validation.handleSubmit}>
          <ModalBody>
            <PermissionForm validation={validation} isEdit={isEdit} />
          </ModalBody>
          <ModalFooter>
            {userPermissions.includes(isEdit ? PermissionEnum.UPDATE_PERMISSION : PermissionEnum.ADD_PERMISSION) && (
              <Button
                type="submit"
                color="primary"
              >
                {isEdit ? "Update" : "Add"}
              </Button>
            )}
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <NewDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Permission"
        message="Are you sure you want to delete this Permission?"
        onConfirm={async () => {
          if (selectedPermissionId) {
            setDeleteModalOpen(false);
            const permissionid = selectedPermissionId;
            setSelectedPermissionId(null);
            try {
              const res = await dispatch(
                deletePermissionThunk(permissionid, { status: 2 })
              );
              if (!res?.success) {
                toast.error(res?.message || "Failed to delete permission");
                return;
              }
              toast.success("Permission deleted successfully!");
              await dispatch(fetchPermissionsThunk());
            } catch (err) {
              console.error("Error deleting permission:", err);
              toast.error("An error occurred. Please try again.");
            }
          }
        }}
      />
    </div>
  );
};

export default PermissionPage;

